import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { useTheme, type Theme } from "@/contexts/theme-context";
import { X, Trash2, ShieldAlert, AlertTriangle, Mail, Star, Moon, Sun } from "lucide-react";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { apiClient } from "@/lib/api-client";
import { getCachedChildren, setCachedChildren } from "@/lib/storage";

import { Header } from "@/components/header";
import { AuthGuard } from "@/components/auth/auth-guard";
import { trackEvent, trackScreenView, AnalyticsEvents } from "@/lib/analytics";
import { Capacitor } from '@capacitor/core';
import { AppReview } from '@capawesome/capacitor-app-review';
import { UpdatePasswordForm } from "@/components/settings/update-password-form";
import { UpdateEmailForm } from "@/components/settings/update-email-form";
// import { CommunicationPreferences } from "@/components/settings/communication-preferences";

import { ManageChildren } from "@/components/settings/manage-children";
import { UsageStats } from "@/components/usage-stats";
import { SubscriptionManagement } from "@/components/subscription-management";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

import type { Child } from "@/lib/types";

function AppearanceSection() {
  const { theme, setTheme } = useTheme();

  const options: { value: Theme; label: string; icon: React.ReactNode }[] = [
    { value: "light", label: "Light", icon: <Sun className="h-4 w-4" /> },
    { value: "dark", label: "Dark", icon: <Moon className="h-4 w-4" /> },
  ];

  return (
    <div className="border rounded-lg p-4">
      <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
        <Moon className="h-5 w-5" /> Appearance
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Choose your preferred color theme.
      </p>
      <div className="flex rounded-lg border bg-muted p-1 gap-1">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => {
              setTheme(option.value);
              trackEvent(AnalyticsEvents.THEME_CHANGED, { theme: option.value });
            }}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${theme === option.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
              }`}
          >
            {option.icon}
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user, isSubscribed, logOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  const [children, setChildren] = useState<Child[]>(() => {
    if (!user?.uid) return [];
    return getCachedChildren(user.uid) || [];
  });
  const [selectedChildId, setSelectedChildId] = usePersistedState<string>('selectedChildId', '');
  const { toast } = useToast();

  // Track screen view on mount
  useEffect(() => {
    trackScreenView('Settings');
  }, []);

  // Handle success messages from URL parameters
  useEffect(() => {
    const message = searchParams.get('message');
    if (message === 'email-updated') {
      toast({
        title: "Email Updated Successfully",
        description: "Your email address has been changed. You can now use it to sign in.",
      });
      // Clean up the URL
      navigate('/settings', { replace: true });
    }
  }, [searchParams, toast, navigate]);

  useEffect(() => {
    if (!user?.uid) {
      setChildren([]);
      setSelectedChildId('');
      return;
    }

    const fetchChildren = async () => {
      try {
        const response = await apiClient.getChildren();
        if (response.success && response.data) {
          const childrenData = response.data;
          setChildren(childrenData);
          setCachedChildren(user.uid, childrenData);

          // Handle selected child logic
          if (childrenData.length === 0) {
            setSelectedChildId('');
          } else if (selectedChildId && !childrenData.some(c => c.id === selectedChildId)) {
            // If the persisted selected child no longer exists, select the first available child
            setSelectedChildId(childrenData[0].id);
          } else if (!selectedChildId && childrenData.length > 0) {
            // If no child is selected but children exist, select the first one
            setSelectedChildId(childrenData[0].id);
          }
        }
      } catch (error) {
        console.error("Error fetching children in settings:", error);
      }
    };

    fetchChildren();
  }, [user?.uid, selectedChildId]);

  const handleProfileSave = async (child: Omit<Child, 'id' | 'userId'> | Child) => {
    if (!user) return;

    try {
      if ('id' in child && child.id) {
        // Update existing child
        const response = await apiClient.updateChild(child as Child);
        if (response.success && response.data) {
          // Refresh children
          const childrenResponse = await apiClient.getChildren();
          if (childrenResponse.success && childrenResponse.data) {
            setChildren(childrenResponse.data);
            setCachedChildren(user.uid, childrenResponse.data);
          }
        }
      } else {
        // Create new child
        const response = await apiClient.createChild(child as Omit<Child, 'id' | 'userId'>);
        if (response.success && response.data) {
          setSelectedChildId(response.data.id);
          // Refresh children
          const childrenResponse = await apiClient.getChildren();
          if (childrenResponse.success && childrenResponse.data) {
            setChildren(childrenResponse.data);
            setCachedChildren(user.uid, childrenResponse.data);
          }
        }
      }
    } catch (error) {
      console.error("Error saving child profile:", error);
    }
  }

  const deleteChild = async (childId: string) => {
    if (!user) return;

    try {
      const response = await apiClient.deleteChild(childId);
      if (response.success) {
        if (selectedChildId === childId) {
          const remainingChildren = children.filter(c => c.id !== childId);
          setSelectedChildId(remainingChildren.length > 0 ? remainingChildren[0].id : '');
        }
        // Refresh children
        const childrenResponse = await apiClient.getChildren();
        if (childrenResponse.success && childrenResponse.data) {
          setChildren(childrenResponse.data);
          setCachedChildren(user.uid, childrenResponse.data);
        }
      }
    } catch (error) {
      console.error("Error deleting child:", error);
    }
  };

  const handleRateApp = async () => {
    trackEvent(AnalyticsEvents.RATE_APP_CLICKED);
    try {
      await AppReview.openAppStore({ appId: '6754461553' });
    } catch (error) {
      console.error("Error opening app store:", error);
    }
  };

  return (
    <AuthGuard>
      <div className="flex min-h-screen w-full flex-col bg-background safe-area-left safe-area-right">
        <Header />
        <main className="flex-1 p-4 md:p-8 pt-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-3xl font-bold">Settings</h1>
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} aria-label="Close settings">
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="space-y-8">
              {/* Rate App Banner */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                <div className="space-y-1">
                  <h3 className="font-semibold text-base flex items-center justify-center sm:justify-start gap-2">
                    <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                    Enjoying LullabAI?
                  </h3>
                  <p className="text-sm text-muted-foreground">Share the love and leave us a review to help other parents sleep better.</p>
                </div>
                <Button onClick={handleRateApp} className="shrink-0">
                  Rate App
                </Button>
              </div>

              <ManageChildren children={children} onSave={handleProfileSave} onDelete={deleteChild} isSubscribed={isSubscribed} />

              {/* Appearance */}
              <AppearanceSection />

              <UpdatePasswordForm />
              <UpdateEmailForm />
              {/* <CommunicationPreferences /> */}
              <UsageStats />

              {/* Subscription management */}
              <SubscriptionManagement />

              {/* Support */}
              <div className="border rounded-lg p-4">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2"><Mail className="h-5 w-5" /> Support</h2>
                <p className="text-sm text-muted-foreground mb-4">Have a question or feedback? We're here to help.</p>
                <Button variant="outline" className="w-full sm:w-auto" asChild>
                  <a href="mailto:support@lullabai.dev" onClick={() => trackEvent(AnalyticsEvents.CONTACT_SUPPORT_CLICKED)}>
                    Contact Support
                  </a>
                </Button>
              </div>

              {/* Danger zone */}
              <div className="border rounded-lg p-4">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-destructive" /> Danger Zone</h2>
                <p className="text-sm text-muted-foreground mb-4">Delete your account and all associated data. This cannot be undone.</p>

                {/* Only show warning if user is actually subscribed (isSubscribed is the source of truth) */}
                {isSubscribed && (
                  <Alert className="mb-4 border-yellow-200 bg-yellow-50">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">
                      You have an active subscription. Please cancel your subscription first before deleting your account.
                    </AlertDescription>
                  </Alert>
                )}

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      className="flex items-center gap-2"
                      disabled={isSubscribed}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently remove your account, subscription, children, sleep logs, goals, and conversations.
                        {isSubscribed && (
                          <span className="block mt-2 font-semibold text-destructive">
                            You must cancel your active subscription before deleting your account.
                          </span>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={isSubscribed}
                        onClick={async () => {
                          trackEvent(AnalyticsEvents.DELETE_ACCOUNT_CONFIRMED);
                          const res = await apiClient.deleteAccount();
                          if (res.success) {
                            toast({ title: 'Account deleted' });
                            // Ensure client state resets
                            await logOut();
                          } else {
                            toast({ variant: 'destructive', title: 'Failed to delete account', description: res.error || 'Please try again' });
                          }
                        }}
                      >
                        Delete Account
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Legal Information */}
              <div className="border rounded-lg p-4">
                <h2 className="text-xl font-semibold mb-2">Legal Updated</h2>
                <p className="text-sm text-muted-foreground mb-4">Important legal information about your account.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() =>
                      navigate("/legal/privacy-policy", {
                        state: { returnTo },
                      })
                    }
                  >
                    Privacy Policy
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      if (Capacitor.getPlatform() === 'ios') {
                        window.open('https://www.apple.com/legal/internet-services/itunes/dev/stdeula/', '_blank');
                      } else {
                        navigate("/legal/terms-conditions", {
                          state: { returnTo },
                        });
                      }
                    }}
                  >
                    {Capacitor.getPlatform() === 'ios' ? 'EULA' : 'Terms & Conditions'}
                  </Button>
                </div>
              </div>

              {/* Development Tools */}
              {process.env.NODE_ENV === 'development' && (
                <div className="border rounded-lg p-4">
                  <h2 className="text-xl font-semibold mb-2">Development</h2>
                  <p className="text-sm text-muted-foreground mb-4">Testing and development tools.</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Link to="/subscription-test">
                      <Button variant="outline" className="w-full sm:w-auto">
                        Test Subscriptions
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </AuthGuard>
  )
}
